import { useState, useCallback } from "react";
import { SearchDataValue, StepId } from "../types";

export type JourneyStepValue = SearchDataValue;
export type JourneySelections = Partial<Record<StepId, JourneyStepValue>>;

const isStepValueEmpty = (value: unknown): boolean => {
	if (value === null || value === undefined) {
		return true;
	}

	if (typeof value === "string") {
		return value.trim() === "";
	}

	if (value instanceof Date) {
		return Number.isNaN(value.getTime());
	}

	if (Array.isArray(value)) {
		return value.every(item => isStepValueEmpty(item));
	}

	if (typeof value === "object") {
		const objectValues = Object.values(value as Record<string, unknown>);
		return objectValues.every(item => isStepValueEmpty(item));
	}

	return false;
};

export const useSearchNavigation = (steps: StepId[], initialSelections: JourneySelections = {}) => {
	const [history, setHistory] = useState<StepId[]>([]);
	const [selections, setSelections] = useState<JourneySelections>(initialSelections);

	const isInitialSelectionsFullyFilled = Object.entries(initialSelections).every(
		([_, value]) => !isStepValueEmpty(value)
	);

	const currentStepId = history.at(-1);
	const currentStepIndex = currentStepId ? steps.indexOf(currentStepId) : -1;

	const startFlow = useCallback((stepId: StepId, currentSelections: JourneySelections = {}) => {
		setHistory([stepId]);
		setSelections(currentSelections);
	}, []);

	const cancelFlow = useCallback(() => {
		setHistory([]);
		setSelections({});
	}, []);

	const updateSelectionForStep = useCallback((stepId: StepId, value: JourneyStepValue) => {
		setSelections(prev => ({ ...prev, [stepId]: value }));
	}, []);

	const updateSelection = useCallback(
		(value: JourneyStepValue) => {
			if (!currentStepId) {
				return;
			}

			updateSelectionForStep(currentStepId, value);
		},
		[currentStepId, updateSelectionForStep]
	);

	const next = useCallback(
		(value: JourneyStepValue) => {
			if (!currentStepId || currentStepIndex === -1) {
				return undefined;
			}

			if (isStepValueEmpty(value)) {
				setSelections(prev => ({
					...prev,
					[currentStepId]: "",
				}));
				return undefined;
			}

			const newSelections = { ...selections, [currentStepId]: value };
			setSelections(newSelections);

			const isFullyFilled =
				Object.entries(newSelections).filter(([_, value]) => isStepValueEmpty(value))
					.length === 0;

			if (isFullyFilled) {
				if (currentStepId === steps.at(-1)) {
					return "CLOSE";
				}

				if (isInitialSelectionsFullyFilled) {
					return "CLOSE";
				}

				if (!isInitialSelectionsFullyFilled) {
					setHistory(steps.slice(currentStepIndex, currentStepIndex + 2));
					return "CONTINUE";
				}
			}

			const nextEmptyStep = steps.find(step => isStepValueEmpty(newSelections[step]));

			if (nextEmptyStep) {
				setHistory(prev => [...prev, nextEmptyStep]);
				return "CONTINUE";
			} else {
				return "CLOSE";
			}
		},
		[currentStepId, currentStepIndex, selections, steps, isInitialSelectionsFullyFilled]
	);

	const back = useCallback(() => {
		if (!currentStepId) {
			return;
		}

		const canBackFollowingStepReverseOrder =
			Object.entries(selections).filter(([_, value]) => isStepValueEmpty(value)).length <= 1;

		if (canBackFollowingStepReverseOrder) {
			const prevSteps = steps.slice(0, currentStepIndex);
			setHistory(prevSteps);
			return;
		}

		if (history.length <= 1) {
			return;
		}

		setHistory(prev => prev.slice(0, -1));
	}, [history, currentStepId, selections, steps, currentStepIndex]);

	return {
		currentStepId,
		selections,
		next,
		back,
		startFlow,
		cancelFlow,
		updateSelection,
		updateSelectionForStep,
		canBack: history.length > 1,
	};
};
